/**
 * ==================================================
 * SHIPPING PROTECTION - FINAL FIXED VERSION
 * ==================================================
 * 
 * This version:
 * - Properly integrates with the cart drawer's update system
 * - Does NOT interfere with quantity changes or item removal
 * - Shipping protection items are completely hidden from cart display (via server-side filtering)
 * - Only updates the cart total price when toggled
 * 
 * Variant ID: 60993185022282
 * ==================================================
 */

class ShippingProtection extends HTMLElement {
  constructor() {
    super();
    
    this.variantId = this.dataset.variantId;
    this.toggle = this.querySelector('input[type="checkbox"]');
    this.priceElement = this.querySelector('[data-protection-price]');
    this.isProcessing = false;
    
    if (this.toggle) {
      this.toggle.addEventListener('change', this.handleToggle.bind(this));
    }
    
    // Listen for cart updates to sync toggle state
    this.cartUpdateHandler = this.handleCartUpdate.bind(this);
    document.addEventListener('theme:cart:change', this.cartUpdateHandler);
    document.addEventListener('theme:cart:refresh', this.cartUpdateHandler);
    document.addEventListener('cart:refresh', this.cartUpdateHandler);
    
    // Initial sync
    this.syncToggleState();
  }
  
  disconnectedCallback() {
    document.removeEventListener('theme:cart:change', this.cartUpdateHandler);
    document.removeEventListener('theme:cart:refresh', this.cartUpdateHandler);
    document.removeEventListener('cart:refresh', this.cartUpdateHandler);
  }
  
  async handleCartUpdate() {
    // Small delay to ensure cart has finished updating
    setTimeout(() => this.syncToggleState(), 50);
  }
  
  async syncToggleState() {
    if (this.isProcessing) return;
    
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const hasProtection = cart.items.some(item => 
        item.variant_id === parseInt(this.variantId, 10)
      );
      
      // Only update toggle if state is different (avoid infinite loops)
      if (this.toggle && this.toggle.checked !== hasProtection) {
        this.toggle.checked = hasProtection;
      }
      
      this.setToggleVisibility(cart);
    } catch (error) {
      // Silent error handling
    }
  }

  async handleToggle(event) {
    if (this.isProcessing) return;
    
    this.isProcessing = true;
    const isChecked = event.target.checked;
    
    this.toggle.disabled = true;
    
    try {
      if (isChecked) {
        await this.addProtection();
      } else {
        await this.removeProtection();
      }
      
      // Use the cart's built-in refresh mechanism
      // This will refresh the cart drawer and update the total price
      this.refreshCart();
      
    } catch (error) {
      // Revert toggle on error
      this.toggle.checked = !isChecked;
      console.error('Shipping protection error:', error);
    } finally {
      this.toggle.disabled = false;
      this.isProcessing = false;
    }
  }

  async addProtection() {
    const formData = {
      items: [{
        id: this.variantId,
        quantity: 1,
        properties: {
          '_shipping_protection': 'true',
          '_hidden_line_item': 'true'
        }
      }]
    };

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      throw new Error('Failed to add protection');
    }

    return await response.json();
  }

  async removeProtection() {
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    const protectionItem = cart.items.find(item => 
      item.variant_id === parseInt(this.variantId)
    );
    
    if (!protectionItem) return;

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: { [protectionItem.key]: 0 }
      })
    });

    if (!response.ok) {
      throw new Error('Failed to remove protection');
    }

    return await response.json();
  }

  refreshCart() {
    // Trigger the cart's built-in refresh mechanism
    // This will update the cart drawer including the total price
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', {
      bubbles: true
    }));
    
    // Also try theme.cart.refresh if available (for older theme versions)
    if (typeof theme !== 'undefined' && theme.cart && typeof theme.cart.refresh === 'function') {
      theme.cart.refresh();
    }
  }
  
  getRealItemCount(cart) {
    const protectionId = parseInt(this.variantId, 10);
    return cart.items
      .filter(item => item.variant_id !== protectionId)
      .reduce((sum, item) => sum + item.quantity, 0);
  }
  
  setToggleVisibility(cart) {
    const realCount = this.getRealItemCount(cart);

    // Hide toggle if no real items in cart
    if (realCount <= 0) {
      this.style.display = 'none';
      if (this.toggle) this.toggle.disabled = true;
      return;
    }

    // Show toggle
    this.style.display = 'block';
    if (this.toggle) this.toggle.disabled = false;
  }
}

customElements.define('shipping-protection', ShippingProtection);
